import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Chip,
  ButtonGroup,
  Stack,
  Avatar
} from '@mui/material';
import {
  School as SchoolIcon,
  Person as PersonIcon,
  SupervisorAccount as GuardianIcon,
  LocalHospital as HealthIcon,
  CheckCircle as SuccessIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Send as SendIcon,
  Language as LanguageIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { useLanguage, Language } from '../../Commun/contexts/LanguageContext';

interface AgencyInfo {
  DOS_LIB?: string;
  DOS_EMAIL?: string;
  DOS_TELPH?: string;
  DOS_ADR1?: string;
  DOS_LOGO?: string;
}

interface ProgramSchedule {
  SSN_DAY_OF_WEEK: number;
  SSN_START_TIME: string;
  SSN_END_TIME: string;
}

interface ProgramOption {
  GRP_IDF: number;
  GRP_NAME: string;
  GRP_START_DATE: string;
  GRP_EXPECTED_END_DATE: string;
  GRP_STATUS: string;
  SCHEDULES: ProgramSchedule[];
}

interface TimeSlotOption {
  start: string;
  end: string;
  label: string;
}

interface CourseOption {
  CRS_IDF: number;
  CRS_TITLE: string;
  CRS_HOURS: number;
  CRS_TOTAL_HOURS?: number;
  CRS_PRICE: number;
  CRS_SHOW_PRICE: boolean;
  CRS_DESCRIPTION: string;
  CRS_AGE_BAND: string;
  CRS_AGE_MIN: number | null;
  CRS_AGE_MAX: number | null;
  PROGRAMS: ProgramOption[];
}

const DAYS_OF_WEEK = [
  { val: 0, key: 'days.sun' },
  { val: 1, key: 'days.mon' },
  { val: 2, key: 'days.tue' },
  { val: 3, key: 'days.wed' },
  { val: 4, key: 'days.thu' },
  { val: 5, key: 'days.fri' },
  { val: 6, key: 'days.sat' },
];

const calculateAge = (dob: string): number | null => {
  if (!dob) return null;
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
  return age;
};

const isRecommendedForAge = (course: CourseOption, age: number | null): boolean => {
  if (age == null) return false;
  if (course.CRS_AGE_MIN == null || course.CRS_AGE_MAX == null) return false;
  return age >= course.CRS_AGE_MIN && age <= course.CRS_AGE_MAX;
};

const ageDistance = (course: CourseOption, age: number): number => {
  if (course.CRS_AGE_MIN != null && course.CRS_AGE_MAX != null) {
    if (age < course.CRS_AGE_MIN) return course.CRS_AGE_MIN - age;
    if (age > course.CRS_AGE_MAX) return age - course.CRS_AGE_MAX;
    return 0;
  }
  if (course.CRS_AGE_MIN != null) return Math.abs(age - course.CRS_AGE_MIN);
  if (course.CRS_AGE_MAX != null) return Math.abs(age - course.CRS_AGE_MAX);
  return 999;
};

const formatScheduleLabel = (
  sched: ProgramSchedule,
  t: (key: string) => string
): string => {
  const day = DAYS_OF_WEEK.find(d => d.val === Number(sched.SSN_DAY_OF_WEEK));
  const dayLabel = day ? t(day.key) : String(sched.SSN_DAY_OF_WEEK);
  const start = (sched.SSN_START_TIME || '').substring(0, 5);
  const end = (sched.SSN_END_TIME || '').substring(0, 5);
  return `${dayLabel} ${start}-${end}`;
};

export const StudentPreRegistrationPage: React.FC = () => {
  const { t, language, setLanguage, dir } = useLanguage();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Agency & Public Config Info
  const [agencyName, setAgencyName] = useState<string>('Blobtic Academy');
  const [agency, setAgency] = useState<AgencyInfo | null>(null);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlotOption[]>([]);

  // Stepper State
  const [activeStep, setActiveStep] = useState<number>(0);
  const [submittedRef, setSubmittedRef] = useState<string | null>(null);

  // Honeypot: invisible field to trap bots (never rendered visibly)
  const [honeypot, setHoneypot] = useState<string>('');

  // Form Fields State
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    firstName: '',
    lastName: '',
    gender: 'male',
    dob: '',
    email: '',
    phone: '',
    address: '',

    // Step 2: Adaptive (Junior / Adult)
    category: 'adult', // 'junior' | 'adult'
    profession: '', // Adult
    emergencyContact: '', // Adult & Junior
    guardianFirstName: '', // Junior
    guardianLastName: '', // Junior
    guardianRelation: 'Père', // Junior
    guardianPhone: '', // Junior
    guardianEmail: '', // Junior
    authorizedPickup: '', // Junior

    // Step 3: Health, Allergies & Image Rights
    hasHealthIssues: undefined as boolean | undefined,
    healthConditions: [] as string[],
    healthOtherDetail: '',
    hasAllergies: undefined as boolean | undefined,
    allergyDetails: '',
    imageRight: undefined as boolean | undefined,

    // Step 4: Course Selection
    selectedCourseId: '',
    selectedGroupId: '',
    timePreference: '',

    // Consent
    termsAccepted: false,
    loi1807Accepted: false
  });

  const steps = [
    t('academy.prereg.page.step1_title'),
    formData.category === 'junior' ? t('academy.prereg.page.step2_junior_title') : t('academy.prereg.page.step2_adult_title'),
    t('academy.prereg.page.step3_title'),
    t('academy.prereg.page.step4_title'),
    t('academy.prereg.page.step5_title')
  ];

  const studentAge = useMemo(() => calculateAge(formData.dob), [formData.dob]);

  const { recommended, other } = useMemo(() => {
    const rec = courses.filter(c => isRecommendedForAge(c, studentAge));
    const rest = courses
      .filter(c => !isRecommendedForAge(c, studentAge))
      .sort((a, b) => ageDistance(a, studentAge ?? 0) - ageDistance(b, studentAge ?? 0));
    return { recommended: rec, other: rest };
  }, [courses, studentAge]);

  const selectedCourse = useMemo(
    () => courses.find(c => String(c.CRS_IDF) === String(formData.selectedCourseId)),
    [courses, formData.selectedCourseId]
  );

  const selectedProgram = useMemo(
    () => selectedCourse?.PROGRAMS?.find(p => String(p.GRP_IDF) === String(formData.selectedGroupId)),
    [selectedCourse, formData.selectedGroupId]
  );

  const availableTimePreferences = useMemo(() => {
    const opts: { value: string; label: string }[] = [];
    const seen = new Set<string>();

    const addOpt = (val: string, lbl: string) => {
      if (val && !seen.has(val)) {
        seen.add(val);
        opts.push({ value: val, label: lbl });
      }
    };

    // 1. Specific schedules from selectedProgram (if any)
    if (selectedProgram?.SCHEDULES?.length) {
      for (const s of selectedProgram.SCHEDULES) {
        const label = formatScheduleLabel(s, t);
        addOpt(label, label);
      }
    }

    // 2. Specific schedules from selectedCourse programs (if any)
    if (selectedCourse?.PROGRAMS?.length) {
      for (const prog of selectedCourse.PROGRAMS) {
        for (const s of prog.SCHEDULES || []) {
          const label = formatScheduleLabel(s, t);
          addOpt(label, label);
        }
      }
    }

    // 3. Backend time slots
    if (timeSlots?.length) {
      for (const ts of timeSlots) {
        addOpt(ts.label, ts.label);
      }
    }

    // 4. Standard default general time preference options (so candidate always has all choices)
    addOpt(t('academy.prereg.time_slot.morning'), t('academy.prereg.time_slot.morning'));
    addOpt(t('academy.prereg.time_slot.afternoon'), t('academy.prereg.time_slot.afternoon'));
    addOpt(t('academy.prereg.time_slot.evening'), t('academy.prereg.time_slot.evening'));
    addOpt(t('academy.prereg.time_slot.weekend'), t('academy.prereg.time_slot.weekend'));
    addOpt(t('academy.prereg.time_slot.flexible'), t('academy.prereg.time_slot.flexible'));

    return opts;
  }, [selectedProgram, selectedCourse, timeSlots, t]);

  const handleCourseSelect = (crsId: number) => {
    setFormData(prev => ({
      ...prev,
      selectedCourseId: String(crsId),
      selectedGroupId: '',
      timePreference: '',
    }));
  };

  const renderCourseCard = (crs: CourseOption, isRecommended: boolean) => {
    const hours = crs.CRS_TOTAL_HOURS ?? crs.CRS_HOURS;
    const isSelected = String(formData.selectedCourseId) === String(crs.CRS_IDF);
    return (
      <Grid item xs={12} sm={6} key={crs.CRS_IDF}>
        <Card
          variant="outlined"
          onClick={() => handleCourseSelect(crs.CRS_IDF)}
          sx={{
            cursor: 'pointer',
            borderRadius: 3,
            borderColor: isSelected ? 'primary.main' : 'divider',
            bgcolor: (theme) =>
              isSelected
                ? theme.palette.mode === 'dark'
                  ? 'rgba(45, 212, 191, 0.12)'
                  : 'rgba(13, 148, 136, 0.04)'
                : theme.palette.mode === 'dark'
                  ? 'rgba(255,255,255,0.03)'
                  : '#fff',
            transition: 'all 0.2s ease',
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Typography variant="h6" fontWeight={700} color="primary">
                {crs.CRS_TITLE}
              </Typography>
              {isRecommended && (
                <Chip
                  label={t('academy.prereg.page.recommended_badge')}
                  color="success"
                  size="small"
                  sx={{ fontWeight: 700 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <Chip
                label={t('academy.prereg.page.hours_label', hours)}
                size="small"
                color="info"
                variant="outlined"
              />
              {crs.CRS_AGE_BAND && (
                <Chip label={crs.CRS_AGE_BAND} size="small" variant="outlined" />
              )}
              {crs.CRS_SHOW_PRICE && crs.CRS_PRICE > 0 && (
                <Chip
                  label={t('academy.prereg.page.price_label', `${crs.CRS_PRICE} DZD`)}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              )}
            </Box>
            {crs.CRS_DESCRIPTION && (
              <Typography variant="body2" color="text.secondary">
                {crs.CRS_DESCRIPTION}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Auto-calculate age and set category (junior < 16, adult >= 16)
  useEffect(() => {
    if (!formData.dob) return;
    const birthDate = new Date(formData.dob);
    if (isNaN(birthDate.getTime())) return;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }

    const calculatedCategory = age < 16 ? 'junior' : 'adult';
    setFormData(prev => ({ ...prev, category: calculatedCategory }));
  }, [formData.dob]);

  // Load public agency & course data
  useEffect(() => {
    document.title = `${agencyName} - Pré-inscription en ligne`;
  }, [agencyName]);

  useEffect(() => {
    const fetchPublicData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`/api/public/7DD983F4E7F84606912921C8FE1602AD/get_public_page?token=${encodeURIComponent(token)}`);
        const result = await response.json();

        if (response.ok && result && result.success && result.data) {
          const item = Array.isArray(result.data) ? result.data[0] : result.data;
          setAgencyName(item?.AGENCY?.DOS_LIB || item?.AGENCY_NAME || 'Blobtic Academy');
          setAgency(item?.AGENCY || null);
          setCourses(item?.COURSES || []);
          setTimeSlots(item?.TIME_SLOTS || []);
        } else {
          setError(result?.message || result?.error || t('academy.prereg.page.error_invalid_token'));
        }
      } catch (err: any) {
        setError(t('academy.prereg.page.error_load_failed'));
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchPublicData();
    } else {
      setLoading(false);
      setError(t('academy.prereg.page.error_no_token'));
    }
  }, [token, t]);

  const handleNext = () => {
    setError(null);

    // Validate Step 0 (Personal Information)
    if (activeStep === 0) {
      if (!formData.firstName.trim() || formData.firstName.trim().length < 2) {
        setError(t('academy.students.validation.first_name_required'));
        return;
      }
      if (!formData.lastName.trim() || formData.lastName.trim().length < 2) {
        setError(t('academy.students.validation.last_name_required'));
        return;
      }
      if (!formData.gender) {
        setError(t('academy.students.validation.gender_required'));
        return;
      }
      if (!formData.dob) {
        setError(t('academy.students.validation.dob_required'));
        return;
      }
      const cleanPhone = formData.phone.replace(/[^\d+]/g, '');
      if (!formData.phone.trim() || cleanPhone.length < 8) {
        setError(t('academy.quiz_public.error_phone_invalid'));
        return;
      }
      if (formData.email.trim() && (!formData.email.includes('@') || !formData.email.includes('.'))) {
        setError(t('academy.quiz_public.error_email_invalid'));
        return;
      }
    }

    // Validate Step 1 (Additional Details: Junior / Adult)
    if (activeStep === 1) {
      if (formData.category === 'junior') {
        if (!formData.guardianFirstName.trim()) {
          setError(t('academy.students.validation.guardian_first_name_required'));
          return;
        }
        if (!formData.guardianLastName.trim()) {
          setError(t('academy.students.validation.guardian_last_name_required'));
          return;
        }
        if (!formData.guardianRelation.trim()) {
          setError(t('academy.students.validation.guardian_relation_required'));
          return;
        }
        const cleanGPhone = formData.guardianPhone.replace(/[^\d+]/g, '');
        if (!formData.guardianPhone.trim() || cleanGPhone.length < 8) {
          setError(t('academy.students.validation.guardian_phone_required'));
          return;
        }
      }
    }

    // Validate Step 3 (Course Selection & Preferred Time Slot)
    if (activeStep === 3) {
      if (!formData.selectedCourseId) {
        setError(t('academy.prereg.page.select_course_required'));
        return;
      }
      if (!formData.timePreference.trim()) {
        setError(t('academy.prereg.page.select_time_preference_required'));
        return;
      }
    }

    setActiveStep(prev => prev + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    setError(null);
    setActiveStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!formData.firstName.trim() || formData.firstName.trim().length < 2 || !formData.lastName.trim() || formData.lastName.trim().length < 2) {
      setError(t('academy.quiz_public.error_name_invalid'));
      return;
    }
    const cleanPhone = formData.phone.replace(/[^\d+]/g, '');
    if (!formData.phone.trim() || cleanPhone.length < 8) {
      setError(t('academy.quiz_public.error_phone_invalid'));
      return;
    }
    if (formData.email.trim() && (!formData.email.includes('@') || !formData.email.includes('.'))) {
      setError(t('academy.quiz_public.error_email_invalid'));
      return;
    }
    if (formData.category === 'junior') {
      const cleanGPhone = formData.guardianPhone.replace(/[^\d+]/g, '');
      if (!formData.guardianPhone.trim() || cleanGPhone.length < 8) {
        setError(t('academy.students.validation.guardian_phone_required'));
        return;
      }
    }
    if (!formData.termsAccepted) {
      setError(t('academy.prereg.page.terms_required_error'));
      return;
    }
    if (!formData.loi1807Accepted) {
      setError(t('academy.prereg.page.loi_18_07_required'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        token: token,
        language: language,
        STD_WEBSITE: honeypot,
        STD_CATEGORY: formData.category,
        STD_FIRST_NAME: formData.firstName,
        STD_LAST_NAME: formData.lastName,
        STD_GENDER: formData.gender,
        STD_DATE_OF_BIRTH: formData.dob,
        STD_ADDRESS: formData.address,
        STD_EMAIL: formData.email,
        STD_PHONE: formData.phone,
        STD_EMERGENCY_CONTACT: formData.emergencyContact,
        STD_PROFESSION: formData.profession,
        STD_GUARDIAN_FIRST_NAME: formData.guardianFirstName,
        STD_GUARDIAN_LAST_NAME: formData.guardianLastName,
        STD_GUARDIAN_RELATION: formData.guardianRelation,
        STD_GUARDIAN_PHONE: formData.guardianPhone,
        STD_GUARDIAN_EMAIL: formData.guardianEmail || null,
        STD_AUTHORIZED_PICKUP: formData.authorizedPickup,
        STD_HAS_HEALTH_ISSUES: formData.hasHealthIssues,
        STD_HEALTH_CONDITIONS: formData.healthConditions.join(','),
        STD_HEALTH_OTHER_DETAIL: formData.healthOtherDetail,
        STD_HAS_ALLERGIES: formData.hasAllergies,
        STD_ALLERGY_DETAILS: formData.allergyDetails,
        STD_IMAGE_RIGHT: formData.imageRight,
        SELECTED_COURSE_ID: formData.selectedCourseId,
        SELECTED_GROUP_ID: formData.selectedGroupId || null,
        STD_TIME_PREFERENCE: formData.timePreference,
        STD_LOI_18_07_CONSENT: formData.loi1807Accepted,
      };

      const response = await fetch('/api/public/7DD983F4E7F84606912921C8FE1602AD/submit_pre_registration', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (result && result.success && result.data) {
        setSubmittedRef(result.data.REFERENCE_CODE || 'PRE-2026-OK');
      } else if (result?.error === 'LOI_18_07_REQUIRED' || result?.code === 'LOI_18_07_REQUIRED') {
        setError(t('academy.prereg.page.loi_18_07_required'));
      } else {
        setError(result?.message || result?.error || t('academy.prereg.page.error_submit_failed'));
      }
    } catch (err: any) {
      setError(t('academy.prereg.page.error_submit_generic'));
    } finally {
      setSubmitting(false);
    }
  };

  const isStepValid = () => {
    if (activeStep === 0) {
      return formData.firstName.trim() !== '' && formData.lastName.trim() !== '' && formData.dob !== '' && formData.phone.trim() !== '';
    }
    if (activeStep === 1) {
      if (formData.category === 'junior') {
        return formData.guardianFirstName.trim() !== '' && formData.guardianLastName.trim() !== '' && formData.guardianPhone.trim() !== '';
      }
      return true;
    }
    if (activeStep === 3) {
      return formData.selectedCourseId !== '' && formData.timePreference.trim() !== '';
    }
    if (activeStep === 4) {
      return formData.termsAccepted && formData.loi1807Accepted;
    }
    return true;
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <CircularProgress size={48} color="primary" />
      </Box>
    );
  }

  if (error && (!courses.length || agencyName === 'Blobtic Academy')) {
    return (
      <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
            <Box sx={{ bgcolor: 'error.light', color: 'error.main', width: 72, height: 72, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
              <Typography variant="h5" fontWeight={900}>404</Typography>
            </Box>
            <Typography variant="h5" fontWeight={800} color="text.primary" sx={{ mb: 1 }}>
              {t('academy.prereg.page.error_invalid_token')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Alert severity="warning" sx={{ textAlign: dir === 'rtl' ? 'right' : 'left', borderRadius: 3 }}>
              {t('academy.prereg.page.error_contact_admin_help')}
            </Alert>
          </Paper>
        </Container>
      </Box>
    );
  }

  if (submittedRef) {
    return (
      <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 8 }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
            <SuccessIcon sx={{ fontSize: 72, color: 'success.main', mb: 2 }} />
            <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ mb: 1 }}>
              {t('academy.prereg.page.success_title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t('academy.prereg.page.success_msg', agencyName)}
            </Typography>

            <Chip
              label={t('academy.prereg.page.reference_code', submittedRef)}
              color="primary"
              variant="outlined"
              sx={{ fontSize: '1rem', py: 2.5, px: 2, borderRadius: 3, fontWeight: 700, mb: 4 }}
            />

            <Alert severity="info" sx={{ textAlign: dir === 'rtl' ? 'right' : 'left', borderRadius: 3, mb: 4 }}>
              <strong>{t('academy.prereg.page.next_steps_title')}</strong> {t('academy.prereg.page.next_steps_msg')}
            </Alert>

            <Button variant="outlined" color="primary" onClick={() => window.location.reload()} sx={{ borderRadius: 2, textTransform: 'none', px: 4 }}>
              {t('academy.prereg.page.new_registration')}
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const renderAgencyHeader = () => {
    if (!agency || (!agency.DOS_LIB && !agency.DOS_TELPH && !agency.DOS_EMAIL && !agency.DOS_ADR1)) return null;

    return (
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          p: 2.5,
          borderRadius: 3,
          mb: 3,
          bgcolor: 'background.paper',
          background: 'linear-gradient(135deg, rgba(14, 116, 144, 0.05) 0%, rgba(59, 130, 246, 0.05) 100%)',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {agency.DOS_LOGO ? (
              <Box
                component="img"
                src={agency.DOS_LOGO}
                alt={agency.DOS_LIB || 'Agency Logo'}
                sx={{ maxHeight: 52, maxWidth: 160, objectFit: 'contain', borderRadius: 1.5 }}
              />
            ) : (
              <Avatar sx={{ bgcolor: 'primary.main', color: '#fff', width: 44, height: 44 }}>
                <BusinessIcon />
              </Avatar>
            )}
            <Box>
              {agency.DOS_LIB && (
                <Typography variant="h6" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2 }}>
                  {agency.DOS_LIB}
                </Typography>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                {agency.DOS_TELPH && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <PhoneIcon fontSize="inherit" color="primary" /> <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{agency.DOS_TELPH}</span>
                  </Typography>
                )}
                {agency.DOS_EMAIL && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <EmailIcon fontSize="inherit" color="primary" /> {agency.DOS_EMAIL}
                  </Typography>
                )}
                {agency.DOS_ADR1 && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}>
                    <LocationIcon fontSize="inherit" color="primary" /> {agency.DOS_ADR1}
                  </Typography>
                )}
              </Stack>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  const renderAgencyFooter = () => {
    return (
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        {agency && (agency.DOS_LIB || agency.DOS_TELPH || agency.DOS_EMAIL || agency.DOS_ADR1) && (
          <Paper
            elevation={0}
            variant="outlined"
            sx={{
              p: 2.5,
              borderRadius: 3,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc',
              textAlign: 'center',
              mb: 2
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
              {t('academy.prereg.page.establishment_label', agency.DOS_LIB || 'Blobtic Academy')}
            </Typography>
            <Stack direction="row" spacing={2.5} justifyContent="center" flexWrap="wrap">
              {agency.DOS_TELPH && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon fontSize="small" color="primary" /> <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'embed' }}>{agency.DOS_TELPH}</span>
                </Typography>
              )}
              {agency.DOS_EMAIL && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <EmailIcon fontSize="small" color="primary" /> {agency.DOS_EMAIL}
                </Typography>
              )}
              {agency.DOS_ADR1 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon fontSize="small" color="primary" /> {agency.DOS_ADR1}
                </Typography>
              )}
            </Stack>
          </Paper>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 600, opacity: 0.85 }}>
          {t('common.copyright')}
        </Typography>
      </Box>
    );
  };

  return (
    <Box dir={dir} sx={{ minHeight: '100vh', bgcolor: 'background.default', py: { xs: 3, md: 6 } }}>
      <Container maxWidth="md">
        {renderAgencyHeader()}
        {/* Top Control Bar (Portal Title, Status & Language) */}
        <Paper elevation={0} variant="outlined" sx={{ p: 2.5, borderRadius: 3, mb: 3, bgcolor: 'background.paper', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={800} color="primary.main">
              {t('academy.prereg.page.public_portal')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {formData.category && (
                <Chip
                  label={formData.category === 'junior' ? t('academy.prereg.page.status_junior') : t('academy.prereg.page.status_adult')}
                  color={formData.category === 'junior' ? 'secondary' : 'default'}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                />
              )}

              {/* Language Selector */}
              <ButtonGroup size="small" variant="outlined">
                {(['fr', 'en', 'ar'] as Language[]).map(lang => (
                  <Button
                    key={lang}
                    variant={language === lang ? 'contained' : 'outlined'}
                    onClick={() => setLanguage(lang)}
                    sx={{ px: 1.5, py: 0.5, fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase' }}
                  >
                    {lang}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
            {error}
          </Alert>
        )}

        {/* Stepper Wizard */}
        <Paper elevation={0} variant="outlined" sx={{ p: { xs: 2.5, md: 4 }, borderRadius: 3, bgcolor: 'background.paper' }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4, display: { xs: 'none', sm: 'flex' } }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 3, textAlign: 'center' }}>
            <Typography variant="subtitle2" color="primary.main" fontWeight={700}>
              {t('academy.prereg.page.step_mobile_label', activeStep + 1, steps.length, steps[activeStep])}
            </Typography>
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* ── STEP 1: Personal Information ── */}
          {activeStep === 0 && (
            <Grid container spacing={2.5}>
              <Box
                component="div"
                aria-hidden="true"
                tabIndex={-1}
                sx={{ position: 'absolute', left: -9999, top: -9999, width: 1, height: 1, overflow: 'hidden' }}
              >
                <TextField
                  label="Website"
                  autoComplete="off"
                  tabIndex={-1}
                  value={honeypot}
                  onChange={e => setHoneypot(e.target.value)}
                />
              </Box>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label={t('academy.prereg.page.first_name')}
                  fullWidth
                  size="small"
                  value={formData.firstName}
                  onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label={t('academy.prereg.page.last_name')}
                  fullWidth
                  size="small"
                  value={formData.lastName}
                  onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl component="fieldset" required>
                  <FormLabel required component="legend" sx={{ fontSize: '0.85rem' }}>{t('academy.prereg.page.gender')}</FormLabel>
                  <RadioGroup row value={formData.gender} onChange={e => setFormData({ ...formData, gender: e.target.value })}>
                    <FormControlLabel value="male" control={<Radio size="small" />} label={t('academy.prereg.page.gender_male')} />
                    <FormControlLabel value="female" control={<Radio size="small" />} label={t('academy.prereg.page.gender_female')} />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label={t('academy.prereg.page.dob')}
                  type="date"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  value={formData.dob}
                  onChange={e => setFormData({ ...formData, dob: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  label={t('academy.prereg.page.phone')}
                  fullWidth
                  size="small"
                  value={formData.phone}
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('academy.prereg.page.email')}
                  type="email"
                  fullWidth
                  size="small"
                  value={formData.email}
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('academy.prereg.page.address')}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  value={formData.address}
                  onChange={e => setFormData({ ...formData, address: e.target.value })}
                />
              </Grid>
            </Grid>
          )}

          {/* ── STEP 2: Adaptive (Junior / Adult) ── */}
          {activeStep === 1 && (
            <Box>
              {formData.category === 'junior' ? (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GuardianIcon /> {t('academy.prereg.page.guardian_header')}
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        label={t('academy.prereg.page.guardian_first_name')}
                        fullWidth
                        size="small"
                        value={formData.guardianFirstName}
                        onChange={e => setFormData({ ...formData, guardianFirstName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        label={t('academy.prereg.page.guardian_last_name')}
                        fullWidth
                        size="small"
                        value={formData.guardianLastName}
                        onChange={e => setFormData({ ...formData, guardianLastName: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        label={t('academy.prereg.page.guardian_relation')}
                        select
                        fullWidth
                        size="small"
                        value={formData.guardianRelation}
                        onChange={e => setFormData({ ...formData, guardianRelation: e.target.value })}
                      >
                        <MenuItem value="Père">{t('academy.prereg.page.guardian_relation_father')}</MenuItem>
                        <MenuItem value="Mère">{t('academy.prereg.page.guardian_relation_mother')}</MenuItem>
                        <MenuItem value="Tuteur Légal">{t('academy.prereg.page.guardian_relation_legal')}</MenuItem>
                        <MenuItem value="Oncle/Tante">{t('academy.prereg.page.guardian_relation_uncle')}</MenuItem>
                        <MenuItem value="Grand-Parent">{t('academy.prereg.page.guardian_relation_grandparent')}</MenuItem>
                      </TextField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        label={t('academy.prereg.page.guardian_phone')}
                        fullWidth
                        size="small"
                        value={formData.guardianPhone}
                        onChange={e => setFormData({ ...formData, guardianPhone: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('academy.prereg.page.guardian_email')}
                        placeholder="parent@email.com"
                        type="email"
                        fullWidth
                        size="small"
                        value={formData.guardianEmail}
                        onChange={e => setFormData({ ...formData, guardianEmail: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={t('academy.prereg.page.authorized_pickup')}
                        placeholder={t('academy.prereg.page.authorized_pickup_placeholder')}
                        fullWidth
                        size="small"
                        multiline
                        rows={3}
                        value={formData.authorizedPickup}
                        onChange={e => setFormData({ ...formData, authorizedPickup: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon /> {t('academy.prereg.page.adult_info_header')}
                  </Typography>
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('academy.prereg.page.profession')}
                        placeholder={t('academy.prereg.page.profession_placeholder')}
                        fullWidth
                        size="small"
                        value={formData.profession}
                        onChange={e => setFormData({ ...formData, profession: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        label={t('academy.prereg.page.emergency_contact')}
                        fullWidth
                        size="small"
                        value={formData.emergencyContact}
                        onChange={e => setFormData({ ...formData, emergencyContact: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}

          {/* ── STEP 3: Health, Allergies & Image Rights ── */}
          {activeStep === 2 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HealthIcon /> {t('academy.prereg.page.health_header')}
              </Typography>

              {/* Card 1: Medical Information (Health Issues) */}
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <HealthIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('academy.prereg.page.health_question')}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {t('academy.prereg.page.health_explanation')}
                </Typography>

                <RadioGroup
                  row
                  value={formData.hasHealthIssues === true ? 'yes' : formData.hasHealthIssues === false ? 'no' : ''}
                  onChange={e => {
                    const val = e.target.value === 'yes';
                    setFormData(prev => ({
                      ...prev,
                      hasHealthIssues: val,
                      healthConditions: val ? prev.healthConditions : [],
                      healthOtherDetail: val ? prev.healthOtherDetail : '',
                    }));
                  }}
                  sx={{ mb: formData.hasHealthIssues ? 2 : 0 }}
                >
                  <FormControlLabel value="yes" control={<Radio size="small" />} label={t('academy.prereg.page.yes')} />
                  <FormControlLabel value="no" control={<Radio size="small" />} label={t('academy.prereg.page.no')} />
                </RadioGroup>

                {formData.hasHealthIssues && (
                  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                    <FormLabel sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 1, display: 'block' }}>
                      {t('academy.prereg.page.health_specify_label')}
                    </FormLabel>
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      {[
                        { key: 'asthma', label: t('academy.prereg.page.condition_asthma') },
                        { key: 'diabetes', label: t('academy.prereg.page.condition_diabetes') },
                        { key: 'epilepsy', label: t('academy.prereg.page.condition_epilepsy') },
                        { key: 'other', label: t('academy.prereg.page.condition_other') },
                      ].map(item => (
                        <Grid item xs={6} sm={3} key={item.key}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={formData.healthConditions.includes(item.key)}
                                onChange={e => {
                                  const checked = e.target.checked;
                                  setFormData(prev => ({
                                    ...prev,
                                    healthConditions: checked
                                      ? [...prev.healthConditions, item.key]
                                      : prev.healthConditions.filter(c => c !== item.key)
                                  }));
                                }}
                              />
                            }
                            label={<Typography variant="body2">{item.label}</Typography>}
                          />
                        </Grid>
                      ))}
                    </Grid>
                    <TextField
                      placeholder={t('academy.prereg.page.health_placeholder')}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                      value={formData.healthOtherDetail}
                      onChange={e => setFormData(prev => ({ ...prev, healthOtherDetail: e.target.value }))}
                    />
                  </Box>
                )}
              </Paper>

              <Grid container spacing={2.5}>
                {/* Card 2: Known Allergies */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: '100%', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {t('academy.prereg.page.allergies_header')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {t('academy.prereg.page.allergies_explanation')}
                    </Typography>

                    <RadioGroup
                      row
                      value={formData.hasAllergies === true ? 'yes' : formData.hasAllergies === false ? 'no' : ''}
                      onChange={e => {
                        const val = e.target.value === 'yes';
                        setFormData(prev => ({
                          ...prev,
                          hasAllergies: val,
                          allergyDetails: val ? prev.allergyDetails : '',
                        }));
                      }}
                      sx={{ mb: formData.hasAllergies ? 2 : 0 }}
                    >
                      <FormControlLabel value="yes" control={<Radio size="small" />} label={t('academy.prereg.page.yes')} />
                      <FormControlLabel value="no" control={<Radio size="small" />} label={t('academy.prereg.page.no')} />
                    </RadioGroup>

                    {formData.hasAllergies && (
                      <TextField
                        placeholder={t('academy.prereg.page.allergies_placeholder')}
                        fullWidth
                        size="small"
                        multiline
                        rows={2}
                        value={formData.allergyDetails}
                        onChange={e => setFormData(prev => ({ ...prev, allergyDetails: e.target.value }))}
                      />
                    )}
                  </Paper>
                </Grid>

                {/* Card 3: Image Rights */}
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3, height: '100%', bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#f8fafc' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {t('academy.prereg.page.image_rights_header')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                      {t('academy.prereg.page.image_rights_explanation')}
                    </Typography>

                    <RadioGroup
                      row
                      value={formData.imageRight === true ? 'yes' : formData.imageRight === false ? 'no' : ''}
                      onChange={e => setFormData(prev => ({ ...prev, imageRight: e.target.value === 'yes' }))}
                    >
                      <FormControlLabel value="yes" control={<Radio size="small" />} label={t('academy.prereg.page.yes')} />
                      <FormControlLabel value="no" control={<Radio size="small" />} label={t('academy.prereg.page.no')} />
                    </RadioGroup>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ── STEP 4: Course Selection ── */}
          {activeStep === 3 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 2 }}>
                {t('academy.prereg.page.course_selection_header')}
              </Typography>

              {courses.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  {t('academy.prereg.page.no_courses')}
                </Alert>
              ) : (
                <>
                  {recommended.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="success.main" sx={{ mb: 1.5 }}>
                        {t('academy.prereg.page.recommended_courses')}
                      </Typography>
                      <Grid container spacing={2}>
                        {recommended.map(crs => renderCourseCard(crs, true))}
                      </Grid>
                    </Box>
                  )}

                  {other.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1.5 }}>
                        {t('academy.prereg.page.other_courses')}
                      </Typography>
                      <Grid container spacing={2}>
                        {other.map(crs => renderCourseCard(crs, false))}
                      </Grid>
                    </Box>
                  )}

                  {recommended.length === 0 && other.length === 0 && (
                    <Grid container spacing={2}>
                      {courses.map(crs => renderCourseCard(crs, false))}
                    </Grid>
                  )}

                  {selectedCourse && (
                    <Box sx={{ mt: 3, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mb: 2 }}>
                        {t('academy.prereg.page.programs_header')}
                      </Typography>

                      {selectedCourse.PROGRAMS?.length > 0 ? (
                        <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                          <FormLabel component="legend" sx={{ fontSize: '0.85rem', mb: 1 }}>
                            {t('academy.prereg.page.select_program')}
                          </FormLabel>
                          <RadioGroup
                            value={formData.selectedGroupId}
                            onChange={e => setFormData(prev => ({
                              ...prev,
                              selectedGroupId: e.target.value,
                              timePreference: '',
                            }))}
                          >
                            {selectedCourse.PROGRAMS.map(prog => (
                              <Box key={prog.GRP_IDF} sx={{ mb: 1.5, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                <FormControlLabel
                                  value={String(prog.GRP_IDF)}
                                  control={<Radio size="small" />}
                                  label={
                                    <Box>
                                      <Typography variant="body2" fontWeight={700}>{prog.GRP_NAME}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        {prog.GRP_START_DATE} → {prog.GRP_EXPECTED_END_DATE} • {prog.GRP_STATUS}
                                      </Typography>
                                    </Box>
                                  }
                                />
                                {prog.SCHEDULES?.length > 0 && (
                                  <Box sx={{ ml: 4, mt: 0.5 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                      {t('academy.prereg.page.schedules_label')}:
                                    </Typography>
                                    {prog.SCHEDULES.map((sched, idx) => (
                                      <Chip
                                        key={idx}
                                        label={formatScheduleLabel(sched, t)}
                                        size="small"
                                        variant="outlined"
                                        sx={{ mr: 0.5, mb: 0.5 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </RadioGroup>
                        </FormControl>
                      ) : (
                        <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                          {t('academy.prereg.page.no_programs')}
                        </Alert>
                      )}

                      <FormControl component="fieldset" required sx={{ width: '100%' }}>
                        <FormLabel component="legend" sx={{ fontSize: '0.85rem', mb: 1 }}>
                          {t('academy.prereg.page.time_preference')}
                        </FormLabel>
                        <RadioGroup
                          value={formData.timePreference}
                          onChange={e => setFormData(prev => ({ ...prev, timePreference: e.target.value }))}
                        >
                          {availableTimePreferences.map(opt => (
                            <FormControlLabel
                              key={opt.value}
                              value={opt.value}
                              control={<Radio size="small" />}
                              label={opt.label}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {/* ── STEP 5: Summary & Confirmation ── */}
          {activeStep === 4 && (
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 3 }}>
                {t('academy.prereg.page.summary_header')}
              </Typography>

              <Paper variant="outlined" sx={{ p: 3, borderRadius: 3, mb: 3, bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">{t('academy.students.table.name')}</Typography>
                    <Typography variant="body1" fontWeight={700}>{formData.firstName} {formData.lastName}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">{t('academy.students.form.category')}</Typography>
                    <Typography variant="body1" fontWeight={700}>{formData.category === 'junior' ? t('academy.prereg.page.status_junior') : t('academy.prereg.page.status_adult')}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">{t('academy.students.phone')}</Typography>
                    <Typography variant="body1" fontWeight={700}>{formData.phone}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">{t('academy.students.email')}</Typography>
                    <Typography variant="body1" fontWeight={700}>{formData.email || '-'}</Typography>
                  </Grid>
                  {formData.category === 'junior' && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">{t('academy.prereg.page.step2_junior_title')}</Typography>
                      <Typography variant="body1" fontWeight={700}>{formData.guardianFirstName} {formData.guardianLastName} ({formData.guardianRelation} - {formData.guardianPhone})</Typography>
                    </Grid>
                  )}
                  {selectedCourse && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">{t('academy.prereg.page.summary_course')}</Typography>
                        <Typography variant="body1" fontWeight={700}>{selectedCourse.CRS_TITLE}</Typography>
                      </Grid>
                      {selectedProgram && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">{t('academy.prereg.page.summary_program')}</Typography>
                          <Typography variant="body1" fontWeight={700}>{selectedProgram.GRP_NAME}</Typography>
                        </Grid>
                      )}
                      {formData.timePreference && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="caption" color="text.secondary">{t('academy.prereg.page.summary_time_pref')}</Typography>
                          <Typography variant="body1" fontWeight={700}>{formData.timePreference}</Typography>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Paper>

              <FormGroup sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.termsAccepted}
                      onChange={e => setFormData({ ...formData, termsAccepted: e.target.checked })}
                    />
                  }
                  label={t('academy.prereg.page.terms_consent')}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.loi1807Accepted}
                      onChange={e => setFormData({ ...formData, loi1807Accepted: e.target.checked })}
                    />
                  }
                  label={t('academy.prereg.page.loi_18_07_consent')}
                />
              </FormGroup>
            </Box>
          )}

          {/* Bottom Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
            <Button
              disabled={activeStep === 0 || submitting}
              onClick={handleBack}
              startIcon={<BackIcon />}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              {t('academy.prereg.page.btn_prev')}
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                disabled={!isStepValid() || submitting}
                onClick={handleSubmit}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                sx={{ textTransform: 'none', borderRadius: 2, px: 4, fontWeight: 700 }}
              >
                {submitting ? t('academy.prereg.page.btn_submitting') : t('academy.prereg.page.btn_submit')}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                endIcon={<NextIcon />}
                sx={{ textTransform: 'none', borderRadius: 2, px: 4, fontWeight: 700 }}
              >
                {t('academy.prereg.page.btn_next')}
              </Button>
            )}
          </Box>
        </Paper>
        {renderAgencyFooter()}
      </Container>
    </Box>
  );
};
